import { formatCents, parseMoneyToCents, tt, type QrCodeMode } from '@agos/shared'

/**
 * A bank transfer taken at the door - `FRONTEND.md` §9.338, `BACKEND.md` §10A.127.
 *
 * **The owner:** *"instead of 'Nezaplatil, Zaplatil', can be only closed and will show šofér thing
 * like… 'waiting for payment'"*, and *"when we received the money, send objednávka as doručená… but
 * notify driver in driver app that it was actually paid and doručené"*.
 *
 * **What this replaces, and why it is not a redesign.** The sheet before it drew a SEPA code that
 * FB-186 measured as unpayable - the stored payee IBAN fails mod-97 - and unverifiable, because
 * `tb_auto_verify` is false with no token and no account id, so the status endpoint answered *not
 * paid* for ever. The two buttons were what was left when nothing could answer. The bank answers
 * now: the code is a TatraPay session's own page, taken with the same credentials CardPay uses.
 *
 * Everything here is a function of the server's answer. **The countdown in particular is the
 * server's**: a deadline started in the browser dies when the app is backgrounded, which on a
 * driver's phone is immediately.
 */

export type TransferPhase = 'waiting' | 'paid' | 'failed'

export interface TransferState {
  phase: TransferPhase
  paymentId: number
  /** The gateway's page, rendered as the QR. Null once it has settled either way. */
  qrUrl: string | null
  amountCents: number
  secondsRemaining: number
  /** The order after this read, so *paid and delivered* is one answer rather than two polls. */
  orderStatus: string
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

/**
 * Read the state, or null when there is nothing readable.
 *
 * **An unrecognised phase reads as `failed`, not as `waiting`.** A screen that cannot understand the
 * answer must not leave a driver standing at a door: the honest thing is to say the transfer did not
 * work and let them take the money another way.
 */
export function transferStateOf(payload: unknown): TransferState | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as Record<string, unknown>
  const phase = text(row.phase)
  if (phase === null) return null
  const seconds = typeof row.secondsRemaining === 'number' ? row.secondsRemaining : 0
  return {
    phase: phase === 'waiting' || phase === 'paid' ? phase : 'failed',
    paymentId: typeof row.paymentId === 'number' ? row.paymentId : 0,
    qrUrl: text(row.qrUrl),
    // Cents, per §14.7 - the API sends a decimal string.
    amountCents: parseMoneyToCents(typeof row.amount === 'string' ? row.amount : 0),
    secondsRemaining: Math.max(0, seconds),
    orderStatus: text(row.orderStatus) ?? '',
  }
}

/** Whether the sheet should keep asking. Nothing changes once it has settled. */
export function keepPolling(state: TransferState | null): boolean {
  return state === null || state.phase === 'waiting'
}

/**
 * The code is drawn only while it can still be paid.
 *
 * A code on screen for a paid order invites a second payment; one for a failed order invites a
 * payment nothing will match to an order. The server withholds the URL in both cases and this
 * agrees with it rather than deciding separately - two rules about when a code is live would drift.
 */
export function showsCode(state: TransferState | null): boolean {
  return state !== null && state.phase === 'waiting' && state.qrUrl !== null
}

/** `9:59`, counting the server's seconds. Never negative - the server clamps and so does this. */
export function countdown(secondsRemaining: number): string {
  const total = Math.max(0, Math.floor(secondsRemaining))
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

/**
 * What the sheet says, in one sentence, for whichever state it is in.
 *
 * **The order's status is in the paid sentence deliberately.** The owner asked to be told the
 * payment arrived *and* that the order went to *doručená*, and the state read answers both - so the
 * screen says both rather than being right about one of them.
 */
export function headline(state: TransferState): string {
  if (state.phase === 'paid') {
    const delivered = state.orderStatus === 'delivered'
    return delivered
      ? `${tt('Zaplatené')} - ${formatCents(state.amountCents)}. ${tt('Objednávka je doručená.')}`
      : `${tt('Zaplatené')} - ${formatCents(state.amountCents)}.`
  }
  if (state.phase === 'failed') {
    return tt('Prevod neprišiel. Vezmite platbu inak - hotovosť alebo karta.')
  }
  return `${tt('Čakám na platbu')} - ${formatCents(state.amountCents)}`
}

/**
 * Said under the code, so the customer is asked for the right thing out loud.
 *
 * **The sentence follows the mode** - §9.402. *Zaplatí vo svojej banke* is true of a SEPA payload
 * and wrong of a gateway session: that code opens TatraPay in the customer's browser and they pick
 * a bank there. A driver reads this out loud to somebody standing in front of them, so the two
 * cases cannot share one sentence.
 *
 * The mode is optional and defaults to `bank`, which keeps every existing caller correct and is the
 * same fallback `codeModeOf` applies.
 */
export function askNote(state: TransferState, mode: QrCodeMode = 'bank'): string {
  const amount = formatCents(state.amountCents)
  return mode === 'gateway'
    ? `${tt('Zákazník naskenuje kód a zaplatí na stránke TatraPay')} ${amount}.`
    : `${tt('Zákazník naskenuje kód a zaplatí vo svojej banke')} ${amount}.`
}

/**
 * The one thing worth repeating on a screen that takes money.
 *
 * Not a caveat on the driver's word any more - there is no word. It is what the countdown means,
 * which is a thing a driver needs to know before they decide to keep standing there.
 */
export function waitingNote(state: TransferState): string {
  if (state.phase !== 'waiting') return ''
  return `${tt('Ak peniaze neprídu do')} ${countdown(state.secondsRemaining)}, ${tt('prevod sa označí ako zlyhaný a môžete vybrať inú platbu.')}`
}
